import React, { SyntheticEvent, useMemo, useState } from "react";

import Autocomplete from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import { JollyRancherChip, type JollyRancherChipColor } from "veneer/Chip";
import CircularProgress from "veneer/CircularProgress";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import Grid from "veneer/Grid";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

import { Application } from "cortex/ui/src/fleet/view/application/model";
import LogsMessageCell from "cortex/ui/src/fleet/view/device/component/LogsMessageCell";
import { DeviceLog } from "cortex/ui/src/fleet/view/device/model";
import { name } from "cortex/ui/src/redux-store/applications/selectors";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import { Option } from "cortex/ui/src/ui/table/types";

const column = createColumnHelper<DeviceLog>();
const filter = createFilter<DeviceLog>("level", "message", "tag", "timestamp");
const LEVEL_OPTIONS: ReadonlyArray<{
  color: JollyRancherChipColor;
  label: string;
  value: string;
}> = [
  { label: "Debug", value: "debug", color: "blue" },
  { label: "Error", value: "error", color: "red" },
  { label: "Info", value: "info", color: "green" },
  { label: "Warning", value: "warn", color: "yellow" },
];

interface LogsTableProps {
  applications: ReadonlyArray<Application>;
  data: ReadonlyArray<DeviceLog>;
  refreshing?: boolean;
  tagOptions?: ReadonlyArray<Option>;
}

export default function LogsTable({
  applications,
  data,
  refreshing,
  tagOptions,
}: LogsTableProps) {
  const [filters, setFilters] = useState<{
    level?: string | null;
    tag?: ReadonlyArray<string> | null;
  }>({});

  const columns = useMemo(
    () => [
      column.accessor("timestamp", {
        header: "Timestamp",
        cell: (prop) =>
          formatDateTimeLocal(prop.getValue(), "MMM Do, YYYY hh:mm:ss a"),
        meta: { width: 200 },
        sortingFn: sorting.datetime((row) => row.timestamp),
      }),
      column.accessor("tag", {
        header: "Tag",
        cell: (prop) => {
          const tag = prop.getValue();
          if (!tag) return "N/A";

          const appName = tag.startsWith("app.") ? tag.substring(4) : null;
          const app = appName
            ? applications.find((application) => application.name === appName)
            : undefined;
          const appLabel = app ? name(app) : tag;

          return (
            <OverflowTooltipTypography variant="body2">
              {appLabel}
            </OverflowTooltipTypography>
          );
        },
        meta: { width: 300 },
        enableSorting: false,
      }),
      column.accessor("level", {
        header: "Level",
        cell: (prop) => {
          const level = prop.getValue();
          const option = LEVEL_OPTIONS.find((o) => o.value === level);
          const color: JollyRancherChipColor = option?.color ?? "gray";
          const label = option ? option.label : level;

          return <JollyRancherChip color={color} label={label} />;
        },
        meta: { width: 120 },
        enableSorting: false,
      }),
      column.accessor("message", {
        header: "Message",
        cell: (prop) => {
          const message = prop.getValue();
          const extra = (prop.row.original as DeviceLog).extra;
          return <LogsMessageCell message={message} rawExtra={extra} />;
        },
        sortingFn: sorting.string((row) => row.message),
      }),
    ],
    [applications],
  );

  const filteredRows = useMemo(() => {
    const { level, tag } = filters;
    return data.filter((log) => {
      if (level && log.level !== level) {
        return false;
      }
      if (tag && tag.length > 0 && !tag.includes(log.tag)) {
        return false;
      }

      return true;
    });
  }, [data, filters]);

  const filterOptions = useMemo(
    () => (
      <FiltersBar
        onChange={setFilters}
        tagOptions={tagOptions}
        value={filters}
      />
    ),
    [tagOptions, filters],
  );

  return (
    <div className="logs-table">
      <DataTable
        columns={columns}
        emptyState={
          <Box p={2} textAlign="center">
            <Typography
              color={Colors.gray1000}
              component="span"
              variant="body2"
            >
              {refreshing ? (
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="center"
                  spacing={1}
                >
                  <CircularProgress size={15} />
                  <Typography color="inherit" component="span" variant="body2">
                    Fetching logs…
                  </Typography>
                </Stack>
              ) : (
                "There is no data to display"
              )}
            </Typography>
          </Box>
        }
        filter={filter}
        getRowProps={() => ({
          hover: true,
        })}
        initialSort={{ id: "timestamp", desc: true }}
        rows={filteredRows}
        viewOptions={filterOptions}
      />
    </div>
  );
}

const FiltersBar = ({
  onChange,
  tagOptions,
  value,
}: {
  onChange: (newValue: {
    level?: string | null;
    tag?: ReadonlyArray<string> | null;
  }) => void;
  tagOptions?: ReadonlyArray<Option>;
  value: {
    level?: string | null;
    tag?: ReadonlyArray<string> | null;
  };
}) => {
  return (
    <div data-table-filters="true">
      <Grid columns={{ xs: 2, xl: 4 }} container spacing={1}>
        <Grid item xs={1}>
          <Autocomplete
            inputProps={{ label: "Log Level" }}
            onChange={(_e: SyntheticEvent, opt: Option | null) =>
              onChange({ ...value, level: opt ? String(opt.value) : null })
            }
            options={LEVEL_OPTIONS}
            size="small"
            value={LEVEL_OPTIONS.find((o) => o.value === value.level) || null}
          />
        </Grid>
        <Grid flex={1} item>
          <Autocomplete
            disableCloseOnSelect
            disabled={!tagOptions || tagOptions.length === 0}
            inputProps={{
              label: "Tag",
              sx: { minWidth: 200, width: "fit-content" },
            }}
            isOptionEqualToValue={(opt, val) =>
              String(opt.value) === String(val.value)
            }
            limitTags={1}
            multiple
            onChange={(_e: SyntheticEvent, opts: Option[] | null) =>
              onChange({
                ...value,
                tag: opts ? opts.map((o) => String(o.value)) : [],
              })
            }
            options={tagOptions || []}
            size="small"
            value={value.tag?.map((tag) => ({ label: tag, value: tag })) || []}
          />
        </Grid>
      </Grid>
    </div>
  );
};
